import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  IconArrowLeft,
  IconCheck,
  IconPlus,
  IconSave,
  IconSpinner,
  IconX,
} from "../icons";
import { notifyError, notifySuccess } from "../notify";
import { useAdminModal } from "../components/AdminModalProvider";

const getDescriptionText = (desc) => {
  if (typeof desc === "string") {
    return desc;
  }
  return desc?.description ?? "";
};

const normalizeDescriptionEntry = (entry) => {
  if (typeof entry === "string") {
    return { id: null, description: entry };
  }
  return {
    id: entry?.id ?? null,
    description: entry?.description ?? "",
  };
};

const normalizeSchemeDescriptions = (schemeDescriptions = {}) =>
  Object.fromEntries(
    Object.entries(schemeDescriptions).map(([type, descriptions]) => [
      type,
      (Array.isArray(descriptions) ? descriptions : []).map(
        normalizeDescriptionEntry
      ),
    ])
  );

export default function SchemeFeatureEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { confirm, prompt } = useAdminModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  // Default empty state structure
  const defaultFormState = {
    featureName: "",
    schemeFeatureDescriptionKey: "",
    categoryId: "",
    schemeFeatureDefinition: "",
    schemeDescriptions: {},
    panelMemberPanelItem: {
      id: null,
      PanelMemberPanelItemCode: "",
      PanelMemberPanelItemLines: [],
    },
  };

  const [formData, setFormData] = useState(defaultFormState);

  useEffect(() => {
    Promise.all([
      fetchCategories(),
      id ? fetchSchemeFeature() : Promise.resolve(),
    ]).finally(() => {
      setLoading(false);
    });
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await fetch("/api/categories", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      notifyError(error.message);
    }
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.trim()) {
      notifyError("Category name cannot be empty");
      return;
    }

    try {
      const response = await fetch("/api/categories", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ name: newCategory.trim() }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to create category");
      }

      const createdCategory = await response.json();
      setCategories((prev) => [...prev, createdCategory]);
      setFormData((prev) => ({
        ...prev,
        categoryId: createdCategory.id,
      }));
      setNewCategory("");
      setShowNewCategoryInput(false);
      notifySuccess("Category created successfully");
    } catch (error) {
      notifyError(error.message);
    }
  };

  const fetchSchemeFeature = async () => {
    try {
      const response = await fetch(`/api/scheme-description/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch scheme feature");
      }
      const data = await response.json();
      // Safely set form data with defaults for missing properties
      setFormData({
        featureName: data.featureName || "",
        schemeFeatureDescriptionKey: data.schemeFeatureDescriptionKey || "",
        categoryId: data.category || "",
        schemeFeatureDefinition: data.schemeFeatureDefinition || "",
        schemeDescriptions: normalizeSchemeDescriptions(data.schemeDescriptions),
        panelMemberPanelItem: {
          id: data.panelMemberPanelItem?.id ?? null,
          PanelMemberPanelItemCode:
            data.panelMemberPanelItem?.PanelMemberPanelItemCode || "",
          PanelMemberPanelItemLines: Array.isArray(
            data.panelMemberPanelItem?.PanelMemberPanelItemLines
          )
            ? data.panelMemberPanelItem.PanelMemberPanelItemLines.map(
                (line) => ({
                  id: line.id ?? null,
                  PanelItemLine: line.PanelItemLine || "",
                  PanelItemLineFormat: line.PanelItemLineFormat || "",
                })
              )
            : [],
        },
      });
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = [];

    // Validate Feature Name
    if (!formData.featureName) {
      errors.push("Feature Name is required");
    }

    // Validate Feature Key
    if (!formData.schemeFeatureDescriptionKey) {
      errors.push("Feature Key is required");
    } else if (formData.schemeFeatureDescriptionKey.length < 7) {
      errors.push("Feature Key must be at least 7 characters long");
    }

    // Validate Category
    if (!formData.categoryId) {
      errors.push("Category is required");
    }

    // Validate Definition
    if (!formData.schemeFeatureDefinition) {
      errors.push("Definition is required");
    }

    // Validate Descriptions
    Object.entries(formData.schemeDescriptions).forEach(
      ([type, descriptions]) => {
        descriptions.forEach((desc, index) => {
          const text = getDescriptionText(desc).trim();
          if (text && text.length < 10) {
            errors.push(
              `Description under "${type}" at position ${index +
                1} must be at least 10 characters long`
            );
          }
        });
      }
    );

    // Validate Panel Member Item
    if (formData.panelMemberPanelItem.PanelMemberPanelItemCode) {
      if (formData.panelMemberPanelItem.PanelMemberPanelItemCode.length > 255) {
        errors.push("Panel Item Code must not exceed 255 characters");
      }

      // Validate Panel Lines if Panel Item Code exists
      formData.panelMemberPanelItem.PanelMemberPanelItemLines.forEach(
        (line, index) => {
          if (!line.PanelItemLine) {
            errors.push(`Panel Line at position ${index + 1} is required`);
          }
        }
      );
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Validate form
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join("\n"));
      }

      // Find the category ID from the selected category name
      const selectedCategory = categories.find(
        (cat) => cat.name === formData.categoryId
      );
      if (!selectedCategory) {
        throw new Error("Invalid category selected");
      }

      // First, update the main scheme feature
      const schemeFeatureResponse = await fetch(
        id ? `/api/scheme-features/${id}` : "/api/scheme-features",
        {
          method: id ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            featureName: formData.featureName.trim(),
            schemeFeatureDescriptionKey: formData.schemeFeatureDescriptionKey.trim(),
            categoryId: selectedCategory.id,
            schemeFeatureDefinition: formData.schemeFeatureDefinition.trim(),
          }),
        }
      );

      if (!schemeFeatureResponse.ok) {
        const errorData = await schemeFeatureResponse.json();
        throw new Error(errorData.message || "Failed to save scheme feature");
      }

      const savedSchemeFeature = await schemeFeatureResponse.json();
      const schemeFeatureId = Number(id || savedSchemeFeature.id);

      // Only process descriptions if they exist and are valid
      if (Object.keys(formData.schemeDescriptions).length > 0) {
        const validDescriptions = Object.entries(
          formData.schemeDescriptions
        ).flatMap(([type, descriptions]) =>
          descriptions
            .map((desc) => ({
              id: desc.id ?? null,
              schemeFeatureId,
              type,
              description: getDescriptionText(desc).trim(),
            }))
            .filter((desc) => desc.description.length >= 10)
        );

        for (const desc of validDescriptions) {
          const isUpdate = Boolean(desc.id);
          const response = await fetch(
            isUpdate
              ? `/api/descriptions/${desc.id}`
              : "/api/descriptions",
            {
              method: isUpdate ? "PUT" : "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              body: JSON.stringify({
                schemeFeatureId: desc.schemeFeatureId,
                type: desc.type,
                description: desc.description,
              }),
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(
              errorData.message || "Failed to save description"
            );
          }
        }
      }

      // Only process panel member item if code exists and is valid
      if (
        formData.panelMemberPanelItem.PanelMemberPanelItemCode.trim() &&
        formData.panelMemberPanelItem.PanelMemberPanelItemCode.length <= 255
      ) {
        const panelItemId = formData.panelMemberPanelItem.id;
        const panelItemPayload = {
          schemeFeatureId,
          panelMemberPanelItemCode:
            formData.panelMemberPanelItem.PanelMemberPanelItemCode.trim(),
        };

        const panelItemResponse = await fetch(
          panelItemId ? `/api/panel-members/${panelItemId}` : "/api/panel-members",
          {
            method: panelItemId ? "PUT" : "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            body: JSON.stringify(panelItemPayload),
          }
        );

        if (!panelItemResponse.ok) {
          throw new Error("Failed to save panel item");
        }

        const savedPanelItem = await panelItemResponse.json();
        const resolvedPanelItemId = panelItemId || savedPanelItem.id;

        const validLines = formData.panelMemberPanelItem.PanelMemberPanelItemLines.filter(
          (line) => line.PanelItemLine.trim()
        );

        for (const line of validLines) {
          const linePayload = {
            panelItemLine: line.PanelItemLine.trim(),
            panelItemLineFormat: line.PanelItemLineFormat?.trim() || null,
          };
          const isLineUpdate = Boolean(line.id);
          const lineResponse = await fetch(
            isLineUpdate
              ? `/api/panel-members/${resolvedPanelItemId}/lines/${line.id}`
              : `/api/panel-members/${resolvedPanelItemId}/lines`,
            {
              method: isLineUpdate ? "PUT" : "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              body: JSON.stringify(linePayload),
            }
          );

          if (!lineResponse.ok) {
            throw new Error("Failed to save panel line");
          }
        }
      }

      notifySuccess("Scheme feature saved successfully");
    } catch (error) {
      notifyError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    if (value === "new") {
      setShowNewCategoryInput(true);
    } else {
      setFormData((prev) => ({
        ...prev,
        categoryId: value,
      }));
    }
  };

  const handleSchemeDescriptionChange = (key, index, value) => {
    setFormData((prev) => ({
      ...prev,
      schemeDescriptions: {
        ...prev.schemeDescriptions,
        [key]: prev.schemeDescriptions[key].map((desc, i) =>
          i === index ? { ...normalizeDescriptionEntry(desc), description: value } : desc
        ),
      },
    }));
  };

  const addSchemeDescription = (key) => {
    setFormData((prev) => ({
      ...prev,
      schemeDescriptions: {
        ...prev.schemeDescriptions,
        [key]: [
          ...(prev.schemeDescriptions[key] || []),
          { id: null, description: "" },
        ],
      },
    }));
  };

  const removeSchemeDescription = async (key, index, descriptionId) => {
    if (descriptionId) {
      const confirmed = await confirm({
        title: "Remove description?",
        description:
          "Are you sure you want to remove this description? It will be deleted from the server.",
        confirmLabel: "Remove",
        tone: "danger",
      });
      if (!confirmed) return;
    }

    if (descriptionId) {
      try {
        const response = await fetch(`/api/descriptions/${descriptionId}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete description");
        }
      } catch (error) {
        notifyError(error.message);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      schemeDescriptions: {
        ...prev.schemeDescriptions,
        [key]: prev.schemeDescriptions[key].filter((_, i) => i !== index),
      },
    }));
  };

  const addNewDescriptionType = async () => {
    const newType = await prompt({
      title: "New description type",
      label: "Description type name",
      placeholder: "e.g. Cover Details",
      confirmLabel: "Add",
    });
    if (newType?.trim()) {
      setFormData((prev) => ({
        ...prev,
        schemeDescriptions: {
          ...prev.schemeDescriptions,
          [newType.trim()]: [],
        },
      }));
    }
  };

  const removeDescriptionType = async (key) => {
    const confirmed = await confirm({
      title: "Remove description type?",
      description: `Are you sure you want to remove "${key}" and all of its descriptions?`,
      confirmLabel: "Remove",
      tone: "danger",
    });
    if (!confirmed) return;

    setFormData((prev) => {
      const newDescriptions = { ...prev.schemeDescriptions };
      delete newDescriptions[key];
      return {
        ...prev,
        schemeDescriptions: newDescriptions,
      };
    });
  };

  const handlePanelItemCodeChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemCode: e.target.value,
      },
    }));
  };

  const handlePanelLineChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemLines: prev.panelMemberPanelItem.PanelMemberPanelItemLines.map(
          (line, i) => (i === index ? { ...line, [field]: value } : line)
        ),
      },
    }));
  };

  const addPanelLine = () => {
    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemLines: [
          ...prev.panelMemberPanelItem.PanelMemberPanelItemLines,
          { id: null, PanelItemLine: "", PanelItemLineFormat: "" },
        ],
      },
    }));
  };

  const removePanelLine = async (index, lineId) => {
    if (lineId) {
      const confirmed = await confirm({
        title: "Remove panel line?",
        description:
          "Are you sure you want to remove this line? It will be deleted from the server.",
        confirmLabel: "Remove",
        tone: "danger",
      });
      if (!confirmed) return;
    }

    if (lineId) {
      try {
        const response = await fetch(
          `/api/panel-members/${formData.panelMemberPanelItem.id}/lines/${lineId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to delete panel line");
        }
      } catch (error) {
        notifyError(error.message);
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      panelMemberPanelItem: {
        ...prev.panelMemberPanelItem,
        PanelMemberPanelItemLines: prev.panelMemberPanelItem.PanelMemberPanelItemLines.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const resetForm = () => {
    setFormData(defaultFormState);
  };

  // Add a cancel handler that resets the form
  const handleCancel = async () => {
    const confirmed = await confirm({
      title: "Discard changes?",
      description:
        "Are you sure you want to cancel? All unsaved changes will be lost.",
      confirmLabel: "Discard",
      cancelLabel: "Keep editing",
      tone: "danger",
    });
    if (!confirmed) return;

    resetForm();
    navigate("/admin/dashboard/scheme-features");
  };

  if (loading) {
    return (
      <div className="loading-container">
        <IconSpinner size={32} />
        <p>Loading scheme feature...</p>
      </div>
    );
  }

  return (
    <div className="scheme-feature-edit">
      <div className="scheme-feature-edit__toolbar">
        <button
          type="button"
          className="scheme-feature-edit__back"
          onClick={() => navigate("/admin/dashboard/scheme-features")}
        >
          <IconArrowLeft size={16} />
          Back to scheme features
        </button>
      </div>

      <form onSubmit={handleSubmit} className="scheme-feature-edit__form">
        <div className="form-sections">
          <section className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label htmlFor="featureName">Feature Name</label>
              <input
                type="text"
                id="featureName"
                name="featureName"
                value={formData.featureName}
                onChange={handleInputChange}
                required
                placeholder="Enter a descriptive name"
              />
              <small className="help-text">
                A user-friendly name for the feature
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="schemeFeatureDescriptionKey">Feature Key</label>
              <input
                type="text"
                id="schemeFeatureDescriptionKey"
                name="schemeFeatureDescriptionKey"
                value={formData.schemeFeatureDescriptionKey}
                onChange={handleInputChange}
                required
                placeholder="Enter feature key (min 7 characters)"
              />
              <small className="help-text">
                Must be at least 7 characters long
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="categoryId">Category</label>
              {showNewCategoryInput ? (
                <div className="new-category-input">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Enter new category name"
                  />
                  <small className="help-text">
                    Category name must be at least 1 character
                  </small>
                  <div className="button-group">
                    <button
                      type="button"
                      className="scheme-feature-edit__btn-primary"
                      onClick={handleAddNewCategory}
                    >
                      <IconCheck size={16} /> Save
                    </button>
                    <button
                      type="button"
                      className="scheme-feature-edit__btn-secondary"
                      onClick={() => {
                        setShowNewCategoryInput(false);
                        setNewCategory("");
                      }}
                    >
                      <IconX size={16} /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <select
                    id="categoryId"
                    value={formData.categoryId || ""}
                    onChange={handleCategoryChange}
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.name}>
                        {category.name}
                      </option>
                    ))}
                    <option value="new">+ Add New Category</option>
                  </select>
                  <small className="help-text">
                    Select an existing category or add a new one
                  </small>
                </>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="schemeFeatureDefinition">Definition</label>
              <textarea
                id="schemeFeatureDefinition"
                name="schemeFeatureDefinition"
                value={formData.schemeFeatureDefinition}
                onChange={handleInputChange}
                required
              />
              <small className="help-text">
                Required field - Provide a clear definition
              </small>
            </div>
          </section>

          <section className="form-section">
            <div className="section-header">
              <h3>Scheme Descriptions</h3>
              <button
                type="button"
                onClick={addNewDescriptionType}
                className="scheme-feature-edit__btn-secondary"
              >
                <IconPlus size={16} /> Add Description Type
              </button>
            </div>
            <small className="help-text section-help">
              Each description must be at least 10 characters long
            </small>
            {Object.entries(formData.schemeDescriptions).map(
              ([key, descriptions]) => (
                <div key={key} className="description-group">
                  <div className="description-header">
                    <h4>{key}</h4>
                    <button
                      type="button"
                      onClick={() => removeDescriptionType(key)}
                      className="scheme-feature-edit__btn-icon"
                      title="Remove this description type"
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                  {descriptions.map((desc, index) => (
                    <div key={index} className="description-item">
                      <input
                        type="text"
                        value={getDescriptionText(desc)}
                        onChange={(e) =>
                          handleSchemeDescriptionChange(
                            key,
                            index,
                            e.target.value
                          )
                        }
                        placeholder="Enter description (min 10 characters)"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          removeSchemeDescription(key, index, desc.id)
                        }
                        className="scheme-feature-edit__btn-icon"
                      >
                        <IconX size={16} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addSchemeDescription(key)}
                    className="scheme-feature-edit__btn-dashed"
                  >
                    <IconPlus size={16} /> Add Description
                  </button>
                </div>
              )
            )}
          </section>

          <section className="form-section">
            <h3>Panel Member Item</h3>
            <small className="help-text section-help">
              Optional section - Fill this if you want to add panel member
              details
            </small>
            <div className="form-group">
              <label htmlFor="panelItemCode">Panel Item Code</label>
              <input
                type="text"
                id="panelItemCode"
                value={formData.panelMemberPanelItem.PanelMemberPanelItemCode}
                onChange={handlePanelItemCodeChange}
                placeholder="Enter panel item code"
              />
              <small className="help-text">Maximum 255 characters</small>
            </div>

            <div className="panel-lines">
              <div className="section-header">
                <h4>Panel Lines</h4>
                <button
                  type="button"
                  onClick={addPanelLine}
                  className="scheme-feature-edit__btn-secondary"
                >
                  <IconPlus size={16} /> Add Line
                </button>
              </div>
              <small className="help-text section-help">
                Panel lines are required if panel item code is provided
              </small>
              {formData.panelMemberPanelItem.PanelMemberPanelItemLines.map(
                (line, index) => (
                  <div key={index} className="panel-line-item">
                    <div className="form-group">
                      <input
                        type="text"
                        value={line.PanelItemLine}
                        onChange={(e) =>
                          handlePanelLineChange(
                            index,
                            "PanelItemLine",
                            e.target.value
                          )
                        }
                        placeholder="Enter panel line (required)"
                      />
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        value={line.PanelItemLineFormat}
                        onChange={(e) =>
                          handlePanelLineChange(
                            index,
                            "PanelItemLineFormat",
                            e.target.value
                          )
                        }
                        placeholder="Enter line format (optional)"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePanelLine(index, line.id)}
                      className="scheme-feature-edit__btn-icon"
                    >
                      <IconX size={16} />
                    </button>
                  </div>
                )
              )}
            </div>
          </section>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="scheme-feature-edit__btn-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="scheme-feature-edit__btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <IconSpinner size={16} /> Saving...
              </>
            ) : (
              <>
                <IconSave size={16} /> Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
